import { useDraggable } from "@dnd-kit/core";
import {
  AlignLeft,
  Archive,
  Clock,
  Flag,
  MoreHorizontal,
} from "lucide-react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { AssigneeAvatars } from "@/components/AssigneeAvatars";
import { ReviewPriorityBadge } from "@/components/taskDetail/ReviewPriority";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type BoardTask, type Urgency } from "@/lib/board";
import { STOPLIGHT } from "@/lib/stoplight";
import { cn } from "@/lib/utils";

// Greenlight's SLA signal, shown as the footer flag. `stale` = sat past its
// threshold; `ok` shows a muted icon-only flag.
const URGENCY_FLAG: Record<Urgency, { label: string; color: string } | null> = {
  stale: { label: "Stale", color: STOPLIGHT.amber },
  ok: null,
};

// Presentational card. Interactivity (click-to-open, drag listeners) is applied
// by the caller via spread props + ref, so the same markup serves the board, the
// drag overlay clone, and the pending placeholder.
export const TaskCard = forwardRef<
  HTMLDivElement,
  {
    task: BoardTask;
    action?: ReactNode;
  } & HTMLAttributes<HTMLDivElement>
>(function TaskCard({ task, action, className, ...props }, ref) {
  const flag = URGENCY_FLAG[task.urgency];
  const isAccepted = task.signoffStatus === "accepted";

  // The footer clock answers "how long has this been waiting in this state?".
  // For changes-requested that's time since it was sent back (`daysInState`);
  // otherwise it's how long the finished work has sat unreviewed (time since it
  // entered ClickUp's complete state). Accepted cards show no clock.
  const changesRequested = task.signoffStatus === "changes_requested";
  const clockDays = isAccepted
    ? null
    : changesRequested
      ? task.daysInState
      : (task.daysSinceComplete ?? task.daysInState);
  const clockTitle = changesRequested
    ? `Sent back ${clockDays}d ago`
    : `Completed ${clockDays}d ago`;
  return (
    <div
      ref={ref}
      className={cn(
        "group relative shrink-0 cursor-pointer rounded-xl border border-border bg-card p-4 shadow-sm transition-all hover:border-foreground/20 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      {...props}
    >
      {action && <div className="absolute right-2 top-2 z-10">{action}</div>}
      <p className="line-clamp-2 pr-6 text-[15px] font-medium leading-snug text-foreground">
        {task.title ?? "Untitled task"}
      </p>

      {task.clickupListName && (
        <p className="mt-2 truncate text-xs text-muted-foreground">
          {task.clickupListName}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3 border-t border-border/60 pt-3 text-xs text-muted-foreground">
        <AssigneeAvatars assignees={task.assignees} />

        <div className="ml-auto flex items-center gap-3">
          <ReviewPriorityBadge level={task.reviewPriority} />
          <span
            className="flex items-center"
            title={task.hasDescription ? "Has a description" : "No description"}
          >
            <AlignLeft
              className={`size-3.5 shrink-0 ${
                task.hasDescription
                  ? "text-muted-foreground"
                  : "text-muted-foreground/25"
              }`}
            />
          </span>
          <span
            className="flex items-center gap-1 font-medium"
            style={flag ? { color: flag.color } : undefined}
            title={flag ? flag.label : "On track"}
          >
            <Flag
              className={`size-3.5 ${flag ? "fill-current" : "text-muted-foreground/50"}`}
            />
            {flag && <span>{flag.label}</span>}
          </span>
          {clockDays != null && (
            <span className="flex items-center gap-1" title={clockTitle}>
              <Clock className="size-3.5" />
              {clockDays}d
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

// The card's ⋯ actions menu. Stops pointer/click/key events from reaching the
// card so opening the menu never starts a drag or opens the task modal.
function CardMenu({ onArchive }: { onArchive: () => void }) {
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Card actions"
          onClick={stop}
          onPointerDown={stop}
          onKeyDown={stop}
          className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground opacity-0 transition hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreHorizontal className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={stop}>
        <DropdownMenuItem onClick={() => onArchive()}>
          <Archive className="size-4" />
          Archive
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// A board card wired for drag-and-drop. PointerSensor's distance constraint lets
// a plain click still open the modal; only a real drag starts a move. Keyboard
// (Enter/Space) opens the card — drag is a pointer-only enhancement.
export function DraggableCard({
  task,
  onOpen,
  onArchive,
  disabled,
}: {
  task: BoardTask;
  onOpen: () => void;
  onArchive?: (id: string) => void;
  disabled: boolean;
}) {
  const { setNodeRef, listeners, isDragging } = useDraggable({
    id: task.id,
    data: { task, from: task.signoffStatus },
    disabled,
  });
  return (
    <TaskCard
      ref={setNodeRef}
      task={task}
      action={onArchive && <CardMenu onArchive={() => onArchive(task.id)} />}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={isDragging ? "opacity-40" : undefined}
      {...listeners}
    />
  );
}
